import { IBusinessIdentifier } from "@faire/web-api--source/indigofair/idv/IBusinessIdentifier";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { StrictLocalizeFunction } from "@faire/web/common/localization";

export const getGlobalInputLabelAndHelper = (
  country: string | undefined,
  strictLocalize: StrictLocalizeFunction
) => {
  const labelMap: Record<
    string,
    {
      label: string;
      helper: string;
      identifierType: IBusinessIdentifier.IdentifierType;
    }
  > = {
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN]: {
      label: strictLocalize({
        id: "j/qgVJ",
        defaultMessage: "Business number",
        description: {
          text: "Label for the business number input field",
          img: "https://cdn.faire.com/fastly/712f6c27f2c1b8f5d59fca965d1210f0a75af0ec5714ef4d9c822b272eff1281.png",
        },
      }),
      helper: strictLocalize({
        id: "QAixH8",
        defaultMessage: "Find this in your registration documents from the CRA",
        description: {
          text: "Helper text explaining where to find the information",
          img: "https://cdn.faire.com/fastly/712f6c27f2c1b8f5d59fca965d1210f0a75af0ec5714ef4d9c822b272eff1281.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.CANADA_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.FRA]: {
      label: strictLocalize({
        id: "oxZG+o",
        defaultMessage: "SIRET/SIREN",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "6SSztg",
        defaultMessage: "Find this in your registration documents from INSEE",
        description: {
          text: "bottom label instructing the user where to find their business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR]: {
      label: strictLocalize({
        id: "NYBagq",
        defaultMessage: "VAT number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "IIp9Wq",
        defaultMessage:
          "Find this on your VAT certificate in the Government Gateway Portal",
        description: {
          text: "bottom label instructing the user where to find their business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.DEU]: {
      label: strictLocalize({
        id: "UvLEDj",
        defaultMessage: "HRA/HRB number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "HGG3hb",
        defaultMessage:
          "Find this in your registration documents from the Amtsgericht",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.ESP]: {
      label: strictLocalize({
        id: "8dt4rw",
        defaultMessage: "NIF number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "BTFjTm",
        defaultMessage:
          "Find this in your tax registration documents from Agencia Tributaria",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.ITA]: {
      label: strictLocalize({
        id: "dnfDnL",
        defaultMessage: "P.IVA number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "+54Gin",
        defaultMessage:
          "Find this in your registration documents from the Agenzia delle Entrate",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.AUT]: {
      label: strictLocalize({
        id: "h9ISrl",
        defaultMessage: "Business Identification Number (UID)",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "1hu0ep",
        defaultMessage:
          "Find this in your registration documents from the Bundesministerium für Finanzen",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.DNK]: {
      label: strictLocalize({
        id: "mTuy6g",
        defaultMessage: "CVR number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "4p8EyM",
        defaultMessage: "Find this in your registration documents from the CVR",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.FIN]: {
      label: strictLocalize({
        id: "Z6vHei",
        defaultMessage: "Business Identification Number (Y-tunnus)",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "kwgf+W",
        defaultMessage: "Find this in your registration documents from the PRH",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.IRL]: {
      label: strictLocalize({
        id: "5CtT/3",
        defaultMessage: "Company registration number (CRN)",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "S9MSZ+",
        defaultMessage: "Find this in your registration documents from the CRO",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.NLD]: {
      label: strictLocalize({
        id: "aAz30v",
        defaultMessage: "KVK number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "raKkIZ",
        defaultMessage: "Find this in your registration documents from the KvK",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.NOR]: {
      label: strictLocalize({
        id: "W90H5d",
        defaultMessage: "Business registration number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "YqPj30",
        defaultMessage:
          "Find this in your registration documents from the Brønnøysundregistrene",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.PRT]: {
      label: strictLocalize({
        id: "t2BURv",
        defaultMessage: "Taxpayer identification number (NIF)",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "sPT6I4",
        defaultMessage:
          "Find this in your registration documents from the Bolagsverket",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.SWE]: {
      label: strictLocalize({
        id: "W90H5d",
        defaultMessage: "Business registration number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "sPT6I4",
        defaultMessage:
          "Find this in your registration documents from the Bolagsverket",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.AUS]: {
      label: strictLocalize({
        id: "W90H5d",
        defaultMessage: "Business registration number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "NJoj0z",
        defaultMessage: "Find this in your registration documents from the ABN",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.BEL]: {
      label: strictLocalize({
        id: "ZaYSyd",
        defaultMessage: "Business number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "mxC6rA",
        defaultMessage:
          "Find this in the registration documents from Kruispuntbank van Ondernemingen",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.LUX]: {
      label: strictLocalize({
        id: "wtieWb",
        defaultMessage: "RCS number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "V2aS/z",
        defaultMessage: "Find this in your business registration from the RCS",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
    [THREE_LETTER_PRIMARY_COUNTRY_CODES.CHE]: {
      label: strictLocalize({
        id: "VY3nfl",
        defaultMessage: "UID number",
        description: {
          text: "header label for the business identification number input field",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      helper: strictLocalize({
        id: "PBULKY",
        defaultMessage:
          "Find this in your registration documents from the Business and Enterprise Register (BER)",
        description: {
          text: "bottom label instructing the user where to find the business identification number",
          img: "https://cdn.faire.com/fastly/8c916c9d53d6668b701826230a3f47dbbdc2c0ba13963444778286166a46fc3a.png",
        },
      }),
      identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
    },
  };

  const fallback = {
    label: strictLocalize({
      id: "wyJjix",
      defaultMessage: "Business certificate or license number",
      description: {
        text: "Label for the business certificate or license number input field",
      },
    }),
    helper: strictLocalize({
      id: "Ye1pTU",
      defaultMessage:
        "Find this on your registration documents issued by the government",
      description: {
        text: "Helper text explaining where to find the information",
      },
    }),
    identifierType: IBusinessIdentifier.IdentifierType.GLOBAL_BUSINESS_NUMBER,
  };

  return labelMap[country ?? ""] ?? fallback;
};
